/* company-detail.jsx — Premium Company Detail for Nexus CRM
   Overrides OverviewTab for Company records only.
   Loaded after overlays.jsx — all globals accessible. */

/* ══════════════════════════════════════════════════════════════
   COMPANY OVERVIEW TAB
   ══════════════════════════════════════════════════════════════ */
const CompanyOverviewTab = ({ record, type, setTab }) => {
  const store    = useStore();
  const pipeline = store.pipeline || [];
  const update   = (field, value) => store.update(type, record.id, { [field]: value });
  const companyActivity = store.activity.filter(a => a.companyId === record.id).slice(0, 3);

  /* O-02: exclude WON / LOST terminal stages so KPI + panel show truly open deals */
  const openDeals = store.deals
    .filter(d => {
      if (d.companyId !== record.id) return false;
      const stg = pipeline.find(s => s.id === d.stageId);
      return !stg || (stg.kind !== 'WON' && stg.kind !== 'LOST');
    })
    .sort((a, b) => b.value - a.value);

  const allOpenTasks = store.tasks.filter(t => !t.done && t.status !== 'cancelled' && t.companyId === record.id);
  const openTasks    = allOpenTasks.slice(0, 4);

  /* O-01: separate full count from display slice so KPI is never capped at 4 */
  const allPeople = store.people.filter(p => p.companyId === record.id);
  const people    = allPeople.slice(0, 4);

  const fmt = n => '$' + (n >= 1000 ? (n / 1000).toFixed(1) + 'K' : n);
  const totalPipe = openDeals.reduce((s, d) => s + d.value, 0);

  return (
    <div className="nx-drawer-overview" style={{ padding: 0 }}>
      <div className="nx-pov-layout">

        {/* ── MAIN column ── */}
        <div className="nx-pov-main">

          {/* AI_DISABLED — re-enable after Enterprise AI Platform launch:
          {typeof AIEntityCard !== 'undefined' && (
            <AIEntityCard record={record} entityType="companies" />
          )} */}

          {/* Company KPI strip */}
          <div className="nx-co-kpi-strip">
            <div className="nx-co-kpi">
              <span className="nx-co-kpi-val">{openDeals.length}</span>
              <span className="nx-co-kpi-lbl">Open Deals</span>
            </div>
            <div className="nx-co-kpi-sep" />
            <div className="nx-co-kpi">
              <span className="nx-co-kpi-val">{fmt(totalPipe)}</span>
              <span className="nx-co-kpi-lbl">Pipeline</span>
            </div>
            <div className="nx-co-kpi-sep" />
            <div className="nx-co-kpi">
              <span className="nx-co-kpi-val">{allPeople.length}</span>
              <span className="nx-co-kpi-lbl">Contacts</span>
            </div>
            <div className="nx-co-kpi-sep" />
            <div className="nx-co-kpi">
              <span className="nx-co-kpi-val" style={record.score != null ? { color: getScoreBand(record.score)?.color } : {}}>{record.score != null ? record.score : '—'}</span>
              <span className="nx-co-kpi-lbl">Company Score</span>
              {record.score != null && <span style={{ fontSize: 10, color: getScoreBand(record.score)?.color, fontWeight: 500, lineHeight: 1.2 }}>{getScoreBand(record.score)?.label}</span>}
            </div>
          </div>

          {/* Company Information */}
          <DrawerPanel title="Company Information">
            <div className="nx-prop-grid">
              <EditableProp label="Domain"    value={record.domain}    mono link onSave={v => update('domain', v)} />
              <EditableProp label="Revenue"   value={record.revenue}   mono      onSave={v => update('revenue', v)} />
              <EditableProp label="Employees" value={record.employees} mono numeric onSave={v => update('employees', Number(v) || 0)} />
              <EditableProp label="Location"  value={record.location}            onSave={v => update('location', v)} />
              <SelectProp   label="Industry"
                value={record.industry}
                options={[...new Set([...store.companies.map(c => c.industry).filter(Boolean), record.industry].filter(Boolean))].sort((a, b) => a.localeCompare(b))}
                onSave={v => update('industry', v)} />
              <Prop label="Open Deals" value={openDeals.length} mono />
            </div>
          </DrawerPanel>

          {/* Key Contacts */}
          <DrawerPanel title="Key Contacts"
            actions={
              <span style={{ display:'flex', gap:'var(--space-2)' }}>
                {allPeople.length > 4 && (
                  <span className="nx-muted nx-mini">+{allPeople.length - 4} more</span>
                )}
                <button type="button" className="nx-panel-link" onClick={() => store.openCreateForm('people', { company: record.name, companyId: record.id })}>
                  <NexusIcon name="Plus" size={10} /> Add
                </button>
              </span>
            }>
            {people.length === 0 ? (
              <div className="nx-empty" style={{ padding: 'var(--space-5) 0' }}>
                <NexusIcon name="Users" size={18} />
                <span>No contacts linked to {record.name}.</span>
              </div>
            ) : (
              <div className="nx-prelated-section">
                {people.map(p => (
                  <button type="button" key={p.id} className="nx-prelated-row" onClick={() => { window.__nx_drawer_back = record; store.openDrawer(p); }}>
                    <Avatar name={p.name} tone={p.tone || 'var(--color-primary-500)'} size={28} />
                    <div className="nx-prelated-body">
                      <span className="nx-prelated-title">{p.name}</span>
                      <span className="nx-prelated-sub">{p.role}</span>
                    </div>
                    <Pill tone={{ New:'gray', Engaged:'blue', Qualified:'amber', Customer:'green' }[p.status] || 'gray'}>
                      {p.status}
                    </Pill>
                  </button>
                ))}
              </div>
            )}
          </DrawerPanel>

          {/* Custom Fields */}
          <DrawerPanel title="Custom Fields">
            {window.CustomFieldsPanel
              ? React.createElement(window.CustomFieldsPanel, { record, entityType: 'companies' })
              : <span className="nx-muted nx-mini" style={{ padding: '8px 0', display: 'block' }}>Custom fields not available.</span>
            }
          </DrawerPanel>

          {/* Recent Activity */}
          <DrawerPanel title="Recent Activity"
            actions={<button type="button" className="nx-panel-link" onClick={() => setTab?.('activity')}>See All <NexusIcon name="ArrowRight" size={11} /></button>}>
            {typeof UniversalTimeline !== 'undefined'
              ? <UniversalTimeline items={companyActivity} compact />
              : <ol className="nx-timeline">
                  {companyActivity.map(a => {
                    const icName = { whatsapp:'MessageCircle', deal:'Target', email:'Mail', note:'StickyNote', call:'Phone', meeting:'Calendar' }[a.type] || 'Activity';
                    const icFg   = { whatsapp:'#0E8C5A', deal:'var(--nx-accent)', email:'#6E3FBA', note:'#C26A1F', call:'#0E8C5A' }[a.type] || 'var(--nx-text-muted)';
                    return (
                      <li key={a.id} className="nx-timeline-item">
                        <span className="nx-timeline-dot" style={{ background: icFg }}><NexusIcon name={icName} size={11} /></span>
                        <div className="nx-timeline-body">
                          <span className="nx-timeline-text"><strong>{a.who}</strong> {a.what}</span>
                          {a.detail && <span className="nx-timeline-detail">{a.detail}</span>}
                          <span className="nx-timeline-time">{a.time}</span>
                        </div>
                      </li>
                    );
                  })}
                </ol>
            }
          </DrawerPanel>

        </div>

        {/* ── SIDE column ── */}
        <div className="nx-pov-side">

          {/* Tags */}
          {typeof TagEditor !== 'undefined' && (
            <DrawerPanel title="Tags">
              <div className="nx-prop-grid">
                <TagEditor record={record} type="companies" />
              </div>
            </DrawerPanel>
          )}

          {/* CRM Snapshot */}
          <DrawerPanel title="CRM Snapshot">
            <div className="nx-crm-snapshot">
              <div className="nx-crm-row">
                <span className="nx-crm-lbl">Lifecycle</span>
                <SelectProp label="" value={record.lifecycle}
                  options={['Lead', 'Account', 'Enterprise']}
                  onSave={v => update('lifecycle', v)} pill="purple" />
              </div>
              {record.lifecycle === 'Account' && (
                <div className="nx-crm-row">
                  <span className="nx-crm-lbl">Parent Enterprise</span>
                  {record.parentCompanyId ? (() => {
                    const parent = store.companies.find(co => co.id === record.parentCompanyId);
                    return parent ? (
                      <button type="button" className="nx-link-val" onClick={() => { window.__nx_drawer_back = record; store.openDrawer(parent); }}>
                        {parent.name}
                      </button>
                    ) : <span className="nx-crm-val nx-muted">—</span>;
                  })() : <span className="nx-crm-val nx-muted">Not assigned</span>}
                </div>
              )}
              <div className="nx-crm-row">
                <span className="nx-crm-lbl">Owner</span>
                <SelectProp label="" value={record.owner}
                  options={store.memberNames?.length ? store.memberNames : [window.__nx_auth?.user?.name].filter(Boolean)}
                  onSave={v => update('owner', v)} avatar />
              </div>
              <div className="nx-crm-row"><span className="nx-crm-lbl">Company Score</span><Score value={record.score} /></div>
              <div className="nx-crm-row"><span className="nx-crm-lbl">Last Activity</span><span className="nx-crm-val">{record.lastActivity || '—'}</span></div>
              <div className="nx-crm-row"><span className="nx-crm-lbl">Revenue</span><EditableProp label="" value={record.revenue} mono onSave={v => update('revenue', v)} /></div>
              <div className="nx-crm-row"><span className="nx-crm-lbl">Created</span><span className="nx-crm-val">{record.createdAt ? new Date(record.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span></div>
            </div>
          </DrawerPanel>

          {/* Open Deals */}
          <DrawerPanel title="Open Deals"
            actions={
              <span style={{ display:'flex', gap:'var(--space-2)' }}>
                {openDeals.length > 0 && (
                  <button type="button" className="nx-panel-link" onClick={() => setTab?.('opportunities')}>
                    See all <NexusIcon name="ArrowRight" size={10} />
                  </button>
                )}
                <button type="button" className="nx-panel-link" onClick={() => store.openCreateForm('deals', { companyId: record.id })}>
                  <NexusIcon name="Plus" size={10} /> Add
                </button>
              </span>
            }>
            {openDeals.slice(0, 3).map(d => {
              const stg = pipeline.find(s => s.id === d.stageId);
              const STAGE_TONE = { WON: 'green', LOST: 'red' };
              const stageTone = STAGE_TONE[stg?.kind] || 'blue';
              return (
                <button type="button" key={d.id} className="nx-mini-deal" onClick={() => { window.__nx_drawer_back = record; store.openDrawer(d); }}>
                  <span className="nx-mini-deal-title">{d.title}</span>
                  <span className="nx-cell is-mono" style={{ fontWeight: 700 }}>{fmt(d.value)}</span>
                  {stg && <Pill tone={stageTone}>{stg.name}</Pill>}
                </button>
              );
            })}
            {openDeals.length === 0 && (
              <div className="nx-muted nx-mini" style={{ padding: 'var(--space-2) 0', display: 'block' }}>No open deals.</div>
            )}
          </DrawerPanel>

          {/* Open Tasks */}
          <DrawerPanel title="Open Tasks"
            actions={
              <span style={{ display:'flex', gap:'var(--space-2)' }}>
                {allOpenTasks.length > 4 && (
                  <button type="button" className="nx-panel-link" onClick={() => setTab?.('tasks')}>
                    See all <NexusIcon name="ArrowRight" size={11} />
                  </button>
                )}
                <button type="button" className="nx-panel-link" onClick={() => store.openCreateForm('tasks', { companyId: record.id })}>
                  <NexusIcon name="Plus" size={10} /> Add
                </button>
              </span>
            }>
            {openTasks.map(t => {
              const PRIORITY_TONE = { High: 'red', Urgent: 'red', Medium: 'amber' };
              const priTone = PRIORITY_TONE[t.priority] || 'gray';
              return (
              <div key={t.id} className={'nx-mini-task' + (t.status === 'in_progress' ? ' is-in-progress' : '')}>
                <button type="button" className="nx-check"
                  onClick={() => store.update('tasks', t.id, { done: !t.done })}>
                  {t.done && <NexusIcon name="Check" size={10} />}
                </button>
                <button type="button" className="nx-task-title" style={{ flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', background:'none', border:'none', padding:0, textAlign:'left', cursor:'pointer', color:'inherit', font:'inherit' }} onClick={() => store.openDrawer(t)}>{t.title}</button>
                {t.status === 'in_progress' && <Pill tone="blue">In Progress</Pill>}
                <Pill tone={priTone}>{t.priority}</Pill>
              </div>
              );
            })}
            {openTasks.length === 0 && (
              <div className="nx-muted nx-mini" style={{ padding: 'var(--space-2) 0', display: 'block' }}>No open tasks.</div>
            )}
          </DrawerPanel>

          {/* Accounts — shown for Enterprise companies that have child Accounts */}
          {record.lifecycle === 'Enterprise' && (() => {
            const childAccounts = store.companies.filter(c => c.parentCompanyId === record.id);
            if (!childAccounts.length) return null;
            return (
              <DrawerPanel title={`Accounts (${childAccounts.length})`}>
                {childAccounts.map(acc => (
                  <button type="button" key={acc.id} className="nx-prelated-row" onClick={() => { window.__nx_drawer_back = record; store.openDrawer(acc); }}>
                    <span className="nx-domain-mark" style={{ width: 28, height: 28, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)' }}>{(acc.name || '?')[0].toUpperCase()}</span>
                    <div className="nx-prelated-body">
                      <span className="nx-prelated-title">{acc.name}</span>
                      <span className="nx-prelated-sub">{[acc.industry, acc.location].filter(Boolean).join(' · ') || '—'}</span>
                    </div>
                    <Pill tone="blue">Account</Pill>
                  </button>
                ))}
              </DrawerPanel>
            );
          })()}

          {/* Company Analytics (if available) */}
          {typeof CompanyAnalyticsPanel !== 'undefined' && CompanyAnalyticsPanel && (
            <DrawerPanel title="Analytics">
              <CompanyAnalyticsPanel company={record} />
            </DrawerPanel>
          )}

          {/* Record Metadata */}
          <DrawerPanel title="Record Metadata">
            <Prop label="Record ID" value={record.id}     mono />
            <Prop label="Created"  value={record.createdAt ? new Date(record.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'} />
            <Prop label="Modified" value={record.updatedAt ? new Date(record.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'} />
          </DrawerPanel>

        </div>

      </div>
    </div>
  );
};
window.CompanyOverviewTab = CompanyOverviewTab;
